import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { AlertCircleIcon } from 'src/components/icons';
import { ModuleType } from 'src/constants';
import { useAppDispatch } from 'src/hooks/useStore';
import { AlertModal } from 'src/legacy/components/Modals';
import { typography15MediumStyle } from 'src/legacy/components/Text';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { DeleteAppBuilderModal } from 'src/routes/appSetup/builder/components/DeleteAppBuilderModal';
import { useListAppBuildersQuery } from 'src/services/api/appBuilderApi';
import {
  useDeleteAppMutation,
  useGetInstallsQuery,
} from 'src/services/api/applicationsApi';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';
import { alertSnackbar } from 'src/store/ui/actions';
import { NonHoverBorder, red, redDark } from 'src/theme/colors';

const useStyles = makeStyles()(() => ({
  actionModal: {
    '& .MuiPaper-root': {
      width: 544,
    },
  },
  embedsContent: {
    color: redDark,
    ...typography15MediumStyle,
  },
}));

// Exported so consumers can style the trigger consistently with the legacy button.
export const removeAppButtonStyles = {
  deleteButton: {
    backgroundColor: red,
    borderColor: red,
    color: '#fff',
    '&:hover': { backgroundColor: red },
  },
  disabledButton: {
    backgroundColor: 'transparent',
    borderColor: NonHoverBorder,
    color: red,
    '&:hover': { backgroundColor: 'transparent' },
  },
};

type AlertModalDataType = {
  modalBody: React.ReactElement;
  modalSuccessActionLabel: string;
  modalTitle: string;
  moduleType?: ModuleType;
  moduleId?: string;
};

interface UseRemoveAppActionParams {
  appModule: { id: string; type: string } | null | undefined;
  /** Custom-app id, used when deleting marketplace/custom apps. */
  appId?: string;
  /** Called after a successful disable/delete. */
  onComplete?: () => void;
}

/**
 * Shared logic for disabling (core apps) or deleting (extension/custom apps)
 * an installed app. Returns a trigger that opens a confirmation modal plus the
 * modal element to render. Mirrors the legacy RemoveApp behavior so callers
 * (settings page bottom button, /app-setup kebab menu) stay in sync.
 */
export const useRemoveAppAction = ({
  appModule,
  appId,
  onComplete,
}: UseRemoveAppActionParams) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { data: { moduleSettings = [], extensionsSettings } = {} } =
    useGetInstallsQuery('withFolders');
  const [deleteCustomApp] = useDeleteAppMutation();
  const { data: appBuilders } = useListAppBuildersQuery();

  const [deletingModuleData, setDeletingModuleData] =
    React.useState<AlertModalDataType | null>(null);
  const [isDeletingModule, setIsDeletingModule] = React.useState(false);
  const [showBuilderDeleteModal, setShowBuilderDeleteModal] =
    React.useState(false);

  const isCoreApp = appModule?.type === 'module';
  const builder = appModule
    ? appBuilders?.find((b) => b.installedModuleId === appModule.id)
    : undefined;

  const openRemoveAppModal = () => {
    if (!appModule) return;

    if (builder) {
      setShowBuilderDeleteModal(true);
      return;
    }

    if (appModule.type === 'module') {
      setDeletingModuleData({
        moduleType: appModule.type as ModuleType,
        moduleId: appModule.id,
        modalTitle: t(($) => $.page_apps.remove_app_modal.disable.confirm),
        modalBody: (
          <BaseTypography fontType="15Regular">
            {t(($) => $.page_apps.remove_app_modal.disable.description)}
          </BaseTypography>
        ),
        modalSuccessActionLabel: t(($) => $.common.disable),
      });
    } else {
      const extensionModuleEmbedCount =
        extensionsSettings?.[appModule.id]?.embedCount ?? 0;

      const stagedModalBody =
        extensionModuleEmbedCount > 0 ? (
          <div>
            <BaseTypography fontType="15Regular">
              The connection data along with &nbsp;
              <span className={classes.embedsContent}>
                {extensionModuleEmbedCount}&nbsp;
                {extensionModuleEmbedCount > 1 ? 'embeds' : 'embed'}
              </span>
              &nbsp; will be deleted.
            </BaseTypography>
          </div>
        ) : (
          <BaseTypography>The connection data will be deleted.</BaseTypography>
        );

      setDeletingModuleData({
        moduleType: (appModule.type === 'authenticated'
          ? 'global'
          : appModule.type) as ModuleType,
        moduleId: appModule.id,
        modalTitle: t(($) => $.page_apps.remove_app_modal.remove.confirm),
        modalBody: stagedModalBody,
        modalSuccessActionLabel: t(($) => $.common.delete),
      });
    }
  };

  const disableApp = async (updatedModuleId: string) => {
    const updatedModules = moduleSettings.map((moduleSettingItem) => {
      if (moduleSettingItem.id === updatedModuleId) {
        return { ...moduleSettingItem, disabled: true };
      }
      return moduleSettingItem;
    });
    await dispatch(updateModuleSettings(updatedModules));
  };

  const cleanupModuleSettings = async (updatedModuleId: string) => {
    const updatedModules = moduleSettings.filter(
      (moduleItem) => moduleItem.id !== updatedModuleId,
    );
    const nonDeletedExtensionsConfigs = Object.entries(extensionsSettings || {})
      .filter(([extensionId]) => extensionId !== updatedModuleId)
      .reduce(
        (prev, [extensionId, extensionConfig]) => ({
          ...prev,
          [extensionId]: extensionConfig,
        }),
        {},
      );
    await dispatch(
      updateModuleSettings(updatedModules, {
        disableSuccessAlert: !!appId,
      }),
    );
    await dispatch(updateExtensionSettingsAction(nonDeletedExtensionsConfigs));
  };

  const deleteApp = async (updatedModuleId: string) => {
    if (appId) {
      await deleteCustomApp({ id: appId });
    }
    await cleanupModuleSettings(updatedModuleId);
  };

  const handleDeleteModuleConfirm = async () => {
    if (!deletingModuleData) return;
    setIsDeletingModule(true);
    const { moduleType, moduleId } = deletingModuleData;
    let failed = false;
    try {
      if (moduleType === 'module') {
        await disableApp(moduleId as string);
      } else {
        await deleteApp(moduleId as string);
      }
    } catch (error) {
      failed = true;
      console.error(error);
      void dispatch(
        alertSnackbar({
          errorMessage:
            moduleType === 'module'
              ? 'Unable to disable app'
              : 'Unable to delete app',
        }),
      );
    } finally {
      setDeletingModuleData(null);
      setIsDeletingModule(false);
    }
    if (!failed) {
      onComplete?.();
    }
  };

  const modal = (
    <>
      {showBuilderDeleteModal && builder && appModule && (
        <DeleteAppBuilderModal
          appId={builder.id}
          appName={builder.name}
          onClose={() => setShowBuilderDeleteModal(false)}
          onDeleted={() => {
            // The backend's DELETE /app-builders/:id already removes the
            // linked install from module_settings + extensionsSettings, and
            // the mutation invalidates the apps cache so getInstalls refetches.
            onComplete?.();
          }}
        />
      )}
      {deletingModuleData && (
        <AlertModal
          className={classes.actionModal}
          open={Boolean(deletingModuleData)}
          title={deletingModuleData.modalTitle}
          successButtonLabel={deletingModuleData.modalSuccessActionLabel}
          onSuccess={handleDeleteModuleConfirm}
          onClose={() => setDeletingModuleData(null)}
          cancelButtonLabel="Cancel"
          Icon={AlertCircleIcon}
          noCancelButton={false}
          isDeleteModal
          isLoading={isDeletingModule}
        >
          {deletingModuleData.modalBody}
        </AlertModal>
      )}
    </>
  );

  return {
    openRemoveAppModal,
    modal,
    isCoreApp,
  };
};
